/**
 * Screen sharing, client side (taken from LogiFlow): one session for the
 * whole tab, which survives page changes in the app.
 *
 * Nothing leaves before the client agrees, and nothing before the support is
 * there: on `support_arrive`, the banner shows, then rrweb (loaded only at
 * that moment) copies the whole page, then sends only the changes, packed and
 * batched. The support only sends back the arrow and "clear": no click and
 * no keystroke ever arrives here. Nothing is recorded anywhere.
 *
 * Reloading: the tab keeps the share id and its phase in sessionStorage
 * (plus the code and its expiry while waiting), nothing else. On start,
 * `resume` reopens the WebSocket and the banner comes back before anything is
 * sent; the server keeps the place for 20 seconds.
 *
 * Always hidden: passwords (rrweb) and everything MASK_SELECTOR targets
 * (bank cards, one-time codes, tokens, keys, secrets, and areas marked
 * `data-masque`): the support sees an empty box instead. Texts, names and
 * phone numbers stay visible.
 */
import { refreshJWTToken } from '@/lib/auth'
import { screenShareApi, shareSocketUrl, type ShareEnd } from '@/api/screenShare'
import { useAuthStore } from '@/stores/useAuthStore'
import { clearArrow, showArrow } from './arrow'

export const MASK_SELECTOR = [
  '[data-masque]',
  'input[autocomplete^="cc-"]',
  'input[autocomplete="one-time-code"]',
  'input[name*="card" i]',
  'input[name*="carte" i]',
  'input[name*="cvc" i]',
  'input[name*="cvv" i]',
  'input[name*="token" i]',
  'input[name*="secret" i]',
  'input[name*="api_key" i]',
  'input[name*="apikey" i]',
  'iframe[src*="stripe.com"]',
].join(', ')

export type Phase = 'idle' | 'confirm' | 'code' | 'waiting' | 'live' | 'ended'

export interface ShareState {
  phase: Phase
  shareId?: number
  code?: string
  /** Code expiry (ms timestamp). */
  expiresAt?: number
  end?: ShareEnd | 'erreur'
  /** The support lost its connection and has 20 seconds to come back. */
  supportAway?: boolean
  /** The support conversation the offer is tied to. */
  conversation?: string
}

interface Kept {
  id: number
  phase: Phase
  code?: string
  expiresAt?: number
}

type Mirror = { getNode(id: number): Node | null }

const SEND_MS = 150
const REREAD_PAGE_MS = 1000
const KEPT_KEY = 'callizy-screen-share'
const KEPT_PHASES: Phase[] = ['code', 'waiting', 'live']
// Reconnection attempts: a little longer, in total, than the server's 20-second grace.
const RETRIES_MS = [500, 1000, 2000, 4000, 8000, 8000]
const PULSE_MS = 10000
const SILENCE_MAX_MS = 25000
// Ended less than a minute ago: during the reload. Say so; older, forget it.
const RECENT_END_S = 60
const END_CODES = ['partage_termine', 'entreprise_coupee', 'acces_refuse', 'duree_atteinte', 'jeton_invalide', 'deja_rejoint']
const TABS_CHANNEL = 'callizy-screen-share'
const SECOND_TAB_MS = 250
// Closed by the server: refused (4001, 4003, 4004, 4009), or replaced by a newer connection (4010).
const FINAL_CLOSES = [4001, 4003, 4004, 4009, 4010]

function currentAddress() {
  return `${window.location.pathname}${window.location.search}`
}

function readKept(): Kept | null {
  try {
    const kept = JSON.parse(window.sessionStorage.getItem(KEPT_KEY) || 'null') as Kept | null
    return kept && typeof kept.id === 'number' && KEPT_PHASES.includes(kept.phase) ? kept : null
  } catch {
    return null
  }
}

function keep(state: ShareState) {
  try {
    if (state.shareId && KEPT_PHASES.includes(state.phase)) {
      const kept: Kept = { id: state.shareId, phase: state.phase }
      if (state.phase === 'code') {
        kept.code = state.code
        kept.expiresAt = state.expiresAt
      }
      window.sessionStorage.setItem(KEPT_KEY, JSON.stringify(kept))
    } else {
      window.sessionStorage.removeItem(KEPT_KEY)
    }
  } catch {
    /* storage refused: no resume after a reload, the rest works */
  }
}

/** One frame on screen: the banner is painted before the first copy. A tab in
 *  the background gets no frame: a short timer takes over, so the support
 *  still gets the page. */
function nextFrame() {
  return new Promise<void>((resolve) => {
    let done = false
    const finish = () => {
      if (done) return
      done = true
      resolve()
    }
    requestAnimationFrame(() => window.setTimeout(finish, 0))
    window.setTimeout(finish, 120)
  })
}

/** A fresh app token for the WebSocket, refreshed if it is about to expire. */
async function freshToken(): Promise<string> {
  const read = () => useAuthStore.getState().accessToken
  const token = read()
  if (!token) throw new Error('no_token')
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    if (payload.exp * 1000 - Date.now() < 60_000) {
      if ((await refreshJWTToken()) !== 'ok') throw new Error('session_expired')
    }
  } catch (err) {
    if ((err as Error).message === 'session_expired') throw err
  }
  const fresh = read()
  if (!fresh) throw new Error('no_token')
  return fresh
}

class ShareSession {
  private state: ShareState = { phase: 'idle' }
  private listeners = new Set<(state: ShareState) => void>()
  private ws: WebSocket | null = null
  private stopRecording: (() => void) | null = null
  private takeSnapshot: (() => void) | null = null
  private mirror: Mirror | null = null
  private buffer: string[] = []
  private fullInBuffer = false
  private timer: number | null = null
  private onResize = () => this.sendPage()
  private pageWatch: number | null = null
  private lastAddress = ''
  private attempt = 0
  private retry: number | null = null
  private pulse: number | null = null
  private lastSign = 0
  private resuming = false
  private resumeStarted = false
  private channel: BroadcastChannel | null = null

  constructor() {
    if (typeof window !== 'undefined') {
      // Sign-out: the share stops, without a message.
      useAuthStore.subscribe((s, before) => {
        if (before.accessToken && !s.accessToken) this.leaveAccount()
      })
      window.addEventListener('pagehide', () => this.pageLeft())
      window.addEventListener('pageshow', (e) => { if (e.persisted) this.pageBack() })
    }
  }

  read(): ShareState {
    return this.state
  }

  subscribe(fn: (state: ShareState) => void): () => void {
    this.listeners.add(fn)
    return () => { this.listeners.delete(fn) }
  }

  private set(state: ShareState) {
    this.state = state
    keep(state)
    this.listeners.forEach((f) => f(state))
  }

  private active() {
    return KEPT_PHASES.includes(this.state.phase)
  }

  /** "Share my screen" in the support widget: first the confirmation window. */
  askConfirmation(conversation?: string) {
    if (this.state.phase === 'idle' || this.state.phase === 'ended') this.set({ phase: 'confirm', conversation })
  }

  cancelConfirmation() {
    if (this.state.phase === 'confirm') this.set({ phase: 'idle' })
  }

  /** After "Share": the code to read over the phone, and waiting for the support. */
  async offer() {
    const share = await screenShareApi.offer(this.state.conversation)
    await this.open(share.id, {
      phase: 'code', code: share.code, expiresAt: Date.now() + (share.expire_dans ?? 600) * 1000,
    })
  }

  /** After "Accept" on a support request: no code. */
  async accept(id: number) {
    await screenShareApi.accept(id)
    await this.open(id, { phase: 'waiting' })
  }

  /** On app start: the share of this tab resumes after a reload. */
  async resume() {
    if (this.resumeStarted || this.ws || this.state.phase !== 'idle') return
    const kept = readKept()
    if (!kept) return
    this.resumeStarted = true
    if (await this.anotherTabHasIt(kept.id)) {
      // Duplicated tab: the share stays with the tab that started it.
      keep({ phase: 'idle' })
      return
    }
    if (this.ws || this.state.phase !== 'idle') return
    this.resuming = true
    await this.open(kept.id, { phase: kept.phase, code: kept.code, expiresAt: kept.expiresAt })
  }

  private async open(shareId: number, start: Omit<ShareState, 'shareId'>) {
    if (this.ws) return
    this.listenToOtherTabs()
    this.attempt = 0
    this.set({ ...start, shareId })
    await this.connect()
  }

  private async connect() {
    const id = this.state.shareId
    if (!id || this.ws || !this.active()) return
    let token: string
    try {
      token = await freshToken()
    } catch {
      this.endHere(this.resuming ? null : 'erreur')
      return
    }
    if (this.ws || !this.active() || this.state.shareId !== id) return
    const ws = new WebSocket(shareSocketUrl(id))
    this.ws = ws
    this.lastSign = Date.now()
    ws.onopen = () => ws.send(JSON.stringify({ type: 'authenticate', token }))
    ws.onmessage = (message) => {
      if (ws !== this.ws) return
      this.lastSign = Date.now()
      try {
        this.receive(JSON.parse(message.data)).catch(() => undefined)
      } catch {
        /* unreadable message: ignored */
      }
    }
    ws.onclose = (event) => {
      if (ws === this.ws) this.connectionLost(event.code)
    }
    this.listenToPulse()
  }

  /** The socket closed while the share is not over: retry, spaced, during the grace. */
  private connectionLost(code: number) {
    this.ws = null
    this.stopTheRecording()
    if (!this.active()) return
    if (code === 4010) {
      this.endHere(null)
      return
    }
    if (FINAL_CLOSES.includes(code) || this.attempt >= RETRIES_MS.length) {
      this.endHere(this.resuming ? null : 'deconnexion')
      return
    }
    const wait = RETRIES_MS[this.attempt]
    this.attempt += 1
    if (this.retry !== null) window.clearTimeout(this.retry)
    this.retry = window.setTimeout(() => {
      this.retry = null
      void this.connect()
    }, wait)
  }

  private pageLeft() {
    const ws = this.ws
    if (!ws) return
    this.ws = null
    ws.onclose = null
    ws.onmessage = null
    ws.close(1000)
    this.stopTheRecording()
  }

  private pageBack() {
    if (!this.active() || this.ws) return
    this.attempt = 0
    void this.connect()
  }

  private listenToPulse() {
    if (this.pulse !== null) return
    this.pulse = window.setInterval(() => {
      const ws = this.ws
      if (!ws) return
      if (Date.now() - this.lastSign > SILENCE_MAX_MS) {
        ws.onclose = null
        ws.onmessage = null
        ws.close()
        this.connectionLost(1006)
      } else if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'ping' }))
      }
    }, PULSE_MS)
  }

  private async receive(message: { type: string; [k: string]: unknown }) {
    switch (message.type) {
      case 'authentifie': {
        this.attempt = 0
        this.resuming = false
        // The support joined with the code during the reload: the code is no longer needed.
        const status = (message.partage as { statut?: string } | undefined)?.statut
        if (this.state.phase === 'code' && status === 'en_cours') {
          this.set({ phase: 'waiting', shareId: this.state.shareId })
        }
        break
      }
      case 'support_arrive':
        // The banner first, the page after.
        this.set({ phase: 'live', shareId: this.state.shareId })
        await nextFrame()
        if (this.state.phase !== 'live' || !this.ws) return
        await this.startRecording()
        this.sendPage()
        break
      case 'absent':
        if (message.role === 'support') {
          // The support reloads its console: nothing leaves until it is back.
          this.stopTheRecording()
          this.set({ ...this.state, supportAway: true })
        }
        break
      case 'retour':
        if (message.role === 'support') this.set({ ...this.state, supportAway: false })
        break
      case 'fleche':
        if (this.mirror) showArrow(this.mirror, Number(message.id), Number(message.dx), Number(message.dy))
        break
      case 'effacer':
        clearArrow()
        break
      case 'termine':
        this.endHere((message.par as ShareEnd) || 'deconnexion')
        break
      case 'erreur': {
        const code = String(message.code)
        if (!END_CODES.includes(code)) break
        const recent = typeof message.il_y_a === 'number' && message.il_y_a <= RECENT_END_S
        if (this.resuming && !recent) {
          this.endHere(null)
        } else {
          this.endHere(code === 'partage_termine' && message.par ? (message.par as ShareEnd) : 'erreur')
        }
        break
      }
      default:
        break
    }
  }

  private async startRecording() {
    if (this.stopRecording) {
      // The support is back: a new full copy.
      this.takeSnapshot?.()
      return
    }
    const [{ record, EventType }, { pack }] = await Promise.all([import('rrweb'), import('@rrweb/packer')])
    if (this.state.phase !== 'live' || this.state.supportAway || !this.ws || this.stopRecording) return
    this.mirror = record.mirror
    this.takeSnapshot = () => record.takeFullSnapshot(true)
    this.stopRecording = record({
      emit: (event) => {
        const full = event.type === EventType.FullSnapshot
        if (full) this.fullInBuffer = true
        // pack() returns a compressed latin-1 string; base64 travels 1.33 times bigger,
        // against 2 to 4 times once escaped in JSON.
        this.buffer.push(btoa(pack(event)))
        this.scheduleSend(full)
      },
      blockSelector: MASK_SELECTOR,
      maskTextSelector: '[data-masque-texte]',
      maskInputOptions: { password: true },
      sampling: { mousemove: 100, scroll: 150, input: 'last' },
      recordCanvas: false,
      collectFonts: false,
      inlineImages: false,
    }) ?? null
    window.addEventListener('resize', this.onResize)
    this.pageWatch = window.setInterval(() => {
      if (currentAddress() !== this.lastAddress) this.sendPage()
    }, REREAD_PAGE_MS)
  }

  private scheduleSend(now: boolean) {
    if (now && this.timer !== null) {
      window.clearTimeout(this.timer)
      this.timer = null
    }
    if (this.timer !== null) return
    this.timer = window.setTimeout(() => this.send(), now ? 0 : SEND_MS)
  }

  private send() {
    this.timer = null
    if (!this.buffer.length || !this.ws || this.ws.readyState !== WebSocket.OPEN) return
    this.ws.send(JSON.stringify({ type: 'evenements', donnees: this.buffer, complet: this.fullInBuffer }))
    this.buffer = []
    this.fullInBuffer = false
  }

  private sendPage() {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return
    this.lastAddress = currentAddress()
    this.ws.send(JSON.stringify({
      type: 'page', url: this.lastAddress, largeur: window.innerWidth, hauteur: window.innerHeight,
    }))
  }

  /** "Stop" (banner) or "Cancel sharing" (code window). */
  stop() {
    this.tellServer()
    this.endHere('client')
  }

  private leaveAccount() {
    if (this.active()) this.tellServer()
    this.endHere(null)
  }

  private tellServer() {
    const id = this.state.shareId
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'terminer' }))
    } else if (id) {
      screenShareApi.stop(id).catch(() => undefined)
    }
  }

  /** The end message was shown. */
  forgetEnd() {
    if (this.state.phase === 'ended') this.set({ phase: 'idle' })
  }

  /** The end, on this side: everything stops and the kept session is erased.
   *  Without `end`, nothing is shown (a resume with nothing left to resume). */
  private endHere(end: ShareEnd | 'erreur' | null) {
    this.cleanUp()
    this.resuming = false
    this.set(end ? { ...this.state, phase: 'ended', end, supportAway: false } : { phase: 'idle' })
  }

  private stopTheRecording() {
    this.stopRecording?.()
    this.stopRecording = null
    this.takeSnapshot = null
    this.mirror = null
    clearArrow()
    window.removeEventListener('resize', this.onResize)
    if (this.pageWatch !== null) window.clearInterval(this.pageWatch)
    this.pageWatch = null
    this.lastAddress = ''
    if (this.timer !== null) window.clearTimeout(this.timer)
    this.timer = null
    this.buffer = []
    this.fullInBuffer = false
  }

  private cleanUp() {
    this.stopTheRecording()
    if (this.retry !== null) window.clearTimeout(this.retry)
    this.retry = null
    if (this.pulse !== null) window.clearInterval(this.pulse)
    this.pulse = null
    const ws = this.ws
    this.ws = null
    if (ws && ws.readyState <= WebSocket.OPEN) ws.close(1000)
  }

  /** Answers other tabs asking whether this share is already taken. */
  private listenToOtherTabs() {
    if (this.channel || typeof BroadcastChannel === 'undefined') return
    this.channel = new BroadcastChannel(TABS_CHANNEL)
    this.channel.onmessage = (m) => {
      const data = m.data as { type?: string; id?: number } | null
      if (data?.type === 'who' && data.id === this.state.shareId && this.active()) {
        this.channel?.postMessage({ type: 'me', id: data.id })
      }
    }
  }

  private anotherTabHasIt(id: number): Promise<boolean> {
    if (typeof BroadcastChannel === 'undefined') return Promise.resolve(false)
    return new Promise((resolve) => {
      const channel = new BroadcastChannel(TABS_CHANNEL)
      const answer = (yes: boolean) => {
        window.clearTimeout(timer)
        channel.close()
        resolve(yes)
      }
      const timer = window.setTimeout(() => answer(false), SECOND_TAB_MS)
      channel.onmessage = (m) => {
        const data = m.data as { type?: string; id?: number } | null
        if (data?.type === 'me' && data.id === id) answer(true)
      }
      channel.postMessage({ type: 'who', id })
    })
  }
}

export const shareSession = new ShareSession()
